import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  List,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  ListItemButton,
  Box,
  Chip,
  Paper,
  Skeleton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon as MenuListItemIcon,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Settings,
  OpenInFull,
  Folder,
  Delete,
  Edit,
  MoreVert,
  Inbox,
  Add,
  DragIndicator,
  History,
} from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useGet } from "../../hooks/api";
import { apiUrls } from "../../utils/urls";
import { useProfile } from "../../context/ProfileContext";
import useDisclosure from "../../hooks/ui/useDisclosure";
import ToDoTemplateFormDialog from "../ToDoTemplateFormDialog";
import api from "../../utils/api";
import ManageToDoDialog from "../ManageToDoDialog";
import ToDoListItem from "./ToDoListItem";
import FullScreenDialog from "../FullScreenDialog/FullScreenDialog";
import { useConfirm } from "material-ui-confirm";
import CompletedToDoItemsDialog from "./CompletedToDoItemsDialog";
import EmployeeImpersonationSelect from "../EmployeeImpersonationSelect";

const PLACEHOLDER_COUNT = 3;
const SIDEBAR_WIDTH = 220;
const INBOX_FOLDER_ID = "inbox";
const INBOX_FOLDER = {
  uuid: INBOX_FOLDER_ID,
  title: "Inbox",
  isSpecial: true,
  item_count: 0,
};

function normalizeFolders(rawFolders = []) {
  const folderById = new Map();

  rawFolders.forEach((folder) => {
    if (!folder?.uuid) {
      return;
    }
    folderById.set(folder.uuid, {
      ...folder,
      isSpecial: folder.uuid === INBOX_FOLDER_ID,
      item_count: folder.item_count ?? 0,
    });
  });

  if (!folderById.has(INBOX_FOLDER_ID)) {
    folderById.set(INBOX_FOLDER_ID, INBOX_FOLDER);
  }

  return Array.from(folderById.values());
}

function ToDoListTitleCard({
  isAdmin,
  manageDisclosure,
  expandDisclosure,
  isFullScreen,
  onAddItem,
  isMobile,
  onOpenFolders,
  selectedFolderId,
  folders,
  impersonationValue,
  onImpersonationChange = () => { },
  onClearImpersonation = () => { },
  isReadOnly = false,
  onOpenCompleted = () => { },
}) {
  const useGenericTitle = isFullScreen && !isMobile;
  const getTitle = () => {
    if (useGenericTitle) {
      return { text: "To-do List", icon: null };
    }
    if (selectedFolderId === INBOX_FOLDER_ID) {
      return {
        text: "Inbox",
        icon: <Inbox sx={{ mr: 1, fontSize: "1.25rem" }} />,
      };
    }
    const folder = folders?.find((f) => f.uuid === selectedFolderId);
    return {
      text: folder?.title ?? "Folder",
      icon: <Folder sx={{ mr: 1, fontSize: "1.25rem" }} />,
    };
  };

  const { text: title, icon: titleIcon } = getTitle();

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ py: isFullScreen ? 1 : "none" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {!useGenericTitle && (
            <Typography
              variant="overline"
              sx={{
                color: (theme) =>
                  theme.palette.mode == "light" && isFullScreen
                    ? "#E0E0E0"
                    : theme.palette.text.secondary,
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              To-do List
            </Typography>
          )}

          <Typography
            variant="h6"
            sx={{
              fontSize: isFullScreen ? "1.25rem" : "1.5rem",
              fontWeight: 700,
              color: (theme) =>
                theme.palette.mode == "light" && isFullScreen
                  ? "#E0E0E0"
                  : theme.palette.text.primary,
              letterSpacing: "-0.025em",
              display: "flex",
              alignItems: "center",
            }}
          >
            {titleIcon}
            {title}
          </Typography>
          {impersonationValue && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                my: 0.5,
              }}
            >
              Viewing {impersonationValue.label} (read-only)
            </Typography>
          )}
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {isAdmin && (
            <EmployeeImpersonationSelect
              value={impersonationValue}
              onChange={onImpersonationChange}
              onClear={onClearImpersonation}
              showCaption={false}
            />
          )}
          {isMobile && (
            <Chip
              label="Folders"
              icon={<Folder />}
              size="small"
              onClick={onOpenFolders}
              sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
              })}
            />
          )}
          {isAdmin && (
            <>
              <Chip
                label="Completed"
                icon={<History />}
                size="small"
                onClick={onOpenCompleted}
                sx={(theme) => ({
                  backgroundColor: theme.palette.background.default,
                })}
              />
              {!isReadOnly && (
                <>
                  <Chip
                    label="Add Item"
                    icon={<Add />}
                    size="small"
                    onClick={onAddItem}
                    variant="filled"
                    sx={(theme) => ({
                      backgroundColor: theme.palette.background.default,
                    })}
                  />
                  <Chip
                    label="Manage"
                    icon={<Settings />}
                    size="small"
                    {...manageDisclosure.getButtonProps()}
                    sx={(theme) => ({
                      backgroundColor: theme.palette.background.default,
                    })}
                  />
                </>
              )}
            </>
          )}
          {!isFullScreen && (
            <Chip
              size="small"
              {...expandDisclosure.getButtonProps()}
              icon={<OpenInFull />}
              label="Expand"
              sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
              })}
            />
          )}
        </Stack>
      </Stack>
      {!isFullScreen && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            mt: 0.5,
            display: "block",
          }}
        >
          Completed items will disappear tomorrow
        </Typography>
      )}
    </>
  );
}

function SortableItem({ id, children, isDisabled = false }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: isDisabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // allow vertical scrolling when not dragging
    touchAction: "pan-y",
    cursor: isDisabled ? "default" : "grab",
    alignSelf: "flex-start",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function SortableFolderItem({
  folder,
  isSelected,
  onSelect,
  onEditFolder,
  onDeleteFolder,
  isAdmin,
  isReadOnly,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: folder.uuid,
    disabled: folder.isSpecial || isReadOnly, // Disable dragging for special folders like Inbox
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(folder.title);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const editInputRef = useRef(null);
  const justStartedEditing = useRef(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Sync edit value when folder title changes
  useEffect(() => {
    setEditValue(folder.title);
  }, [folder.title]);

  const handleEditStart = (event) => {
    if (isReadOnly) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    setIsEditing(true);
    setEditValue(folder.title);
    setMenuAnchor(null);
    justStartedEditing.current = true;

    setTimeout(() => {
      justStartedEditing.current = false;
    }, 100);
  };

  const handleEditSave = () => {
    if (justStartedEditing.current) {
      return;
    }

    if (editValue.trim() && editValue.trim() !== folder.title) {
      onEditFolder(folder, { title: editValue.trim() });
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditValue(folder.title);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleEditSave();
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  };

  const handleMenuOpen = (e) => {
    if (isReadOnly) {
      return;
    }
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDelete = (e) => {
    if (isReadOnly) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    setMenuAnchor(null);
    onDeleteFolder(folder.uuid);
  };

  const handleFolderClick = () => {
    if (!isEditing) {
      onSelect(folder.uuid);
    }
  };

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 0.25,
        backgroundColor: isSelected ? "primary.main" : "transparent",
        borderRadius: 0.75,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isSelected ? "primary.main" : "action.hover",
        },
      }}
    >
      <ListItemButton
        onClick={handleFolderClick}
        sx={{
          py: 0.75,
          px: 1.5,
          borderRadius: 0.75,
          minHeight: 36,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {!folder.isSpecial && !isReadOnly && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 1,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicator fontSize="small" sx={{ color: "text.secondary" }} />
          </Box>
        )}

        <ListItemIcon sx={{ minWidth: 32 }}>
          {folder.isSpecial && folder.uuid === INBOX_FOLDER_ID ? (
            <Inbox
              sx={{
                color: isSelected ? "primary.contrastText" : "text.secondary",
                fontSize: "1.2rem",
              }}
            />
          ) : (
            <Folder
              sx={{
                color: isSelected ? "primary.contrastText" : "text.secondary",
                fontSize: "1.2rem",
              }}
            />
          )}
        </ListItemIcon>

        {isEditing ? (
          <TextField
            ref={editInputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyPress}
            size="small"
            variant="standard"
            autoFocus
            sx={{
              flexGrow: 1,
              mr: 1,
              "& .MuiInputBase-input": {
                fontSize: "0.875rem",
                fontWeight: 500,
              },
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        ) : (
          <ListItemText
            primary={folder.title}
            sx={{
              "& .MuiListItemText-primary": {
                fontSize: "0.875rem",
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? "primary.contrastText" : "text.primary",
              },
            }}
          />
        )}

        <Typography
          variant="caption"
          sx={{
            color: isSelected ? "primary.contrastText" : "text.secondary",
            minWidth: 20,
            textAlign: "right",
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
        >
          {folder.item_count ?? 0}
        </Typography>

        {isAdmin && !folder.isSpecial && !isReadOnly && (
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              ml: 1,
              opacity: 0.7,
              "&:hover": { opacity: 1 },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}

        {isAdmin && !folder.isSpecial && !isReadOnly && (
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={handleEditStart}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <MenuListItemIcon>
                <Edit fontSize="small" />
              </MenuListItemIcon>
              Edit Name
            </MenuItem>
            <MenuItem
              onClick={handleDelete}
              sx={{ color: "error.main" }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <MenuListItemIcon>
                <Delete fontSize="small" />
              </MenuListItemIcon>
              Delete Folder
            </MenuItem>
          </Menu>
        )}
      </ListItemButton>
    </Box>
  );
}

function ToDoSidebar({
  folders,
  selectedFolderId,
  onSelectFolder,
  onEditFolder,
  onDeleteFolder,
  onAddFolder,
  isAdmin,
  isMobile = false,
  folderDialogDisclosure,
  onFolderDragEnd,
  isReadOnly,
}) {
  const allFolders = folders;
  const sortableFolderIds = useMemo(
    () =>
      folders
        .filter((folder) => !folder.isSpecial)
        .map((folder) => folder.uuid),
    [folders]
  );

  const handleFolderSelect = (folderId) => {
    onSelectFolder(folderId);
    if (isMobile && folderDialogDisclosure) {
      folderDialogDisclosure.onClose();
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 0 },
    })
  );

  const sidebarContent = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onFolderDragEnd}
    >
      <SortableContext
        items={sortableFolderIds}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "background.default" : "#fafafa",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: "background.paper",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Folders
              </Typography>
              {isAdmin && (
                <IconButton
                  size="small"
                  onClick={onAddFolder}
                  disabled={isReadOnly}
                  sx={{
                    p: 0.5,
                    opacity: 0.7,
                    "&:hover": {
                      opacity: 1,
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", py: 0.5 }}>
            <List dense disablePadding sx={{ px: 1 }}>
              {allFolders.map((folder) => (
                <SortableFolderItem
                  key={folder.uuid}
                  folder={folder}
                  isSelected={selectedFolderId === folder.uuid}
                  onSelect={handleFolderSelect}
                  onEditFolder={onEditFolder}
                  onDeleteFolder={onDeleteFolder}
                  isAdmin={isAdmin}
                  isReadOnly={isReadOnly}
                />
              ))}
            </List>
          </Box>
        </Box>
      </SortableContext>
    </DndContext>
  );

  if (isMobile) {
    return (
      <Dialog
        {...folderDialogDisclosure.getDisclosureProps()}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: "80vh",
              maxHeight: "600px",
            },
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Folders
            </Typography>
            {isAdmin && (
              <IconButton
                size="small"
                onClick={onAddFolder}
                disabled={isReadOnly}
                sx={{
                  p: 0.5,
                  opacity: 0.7,
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onFolderDragEnd}
          >
            <SortableContext
              items={sortableFolderIds}
              strategy={verticalListSortingStrategy}
            >
              <List dense disablePadding sx={{ px: 1 }}>
                {allFolders.map((folder) => (
                  <SortableFolderItem
                    key={folder.uuid}
                    folder={folder}
                    isSelected={selectedFolderId === folder.uuid}
                    onSelect={handleFolderSelect}
                    onEditFolder={onEditFolder}
                    onDeleteFolder={onDeleteFolder}
                    isAdmin={isAdmin}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Box
      sx={{
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      {sidebarContent}
    </Box>
  );
}

function ToDoItemsList({
  selectedFolderId,
  impersonatedProfileUuid,
  isReadOnly,
  folders,
  reloadToken,
}) {
  const confirm = useConfirm();
  const searchParams = useMemo(() => {
    if (selectedFolderId !== INBOX_FOLDER_ID) {
      return {
        folder_id: selectedFolderId,
      };
    }
    return {
      folder__isnull: true,
    };
  }, [selectedFolderId]);

  const { data, isLoading, refetch } = useGet(
    apiUrls.scheduling.toDoItems(impersonatedProfileUuid),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      params: {
        keep_related: true,
        sign_images: true,
        search_params: searchParams,
        order_by: ["-created_at"],
        select_related: [
          "template",
          "folder",
          "coverage",
          "event_trigger",
          "delegated_by",
          "delegated_by__user",
          "completed_by",
          "completed_by__user",
          "coordination",
          "coordination__completed_by",
          "coordination__completed_by__user",
          "coverage__coveree",
          "coverage__coveree__user",
        ],
        prefetch_related: ["template__images"],
        values: [
          "uuid",
          "user_profile__uuid",
          "event_trigger",
          "template",
          "coordination__uuid",
          "coordination__completed_by",
          "coordination__completed_by__user__first_name",
          "coordination__completed_by__user__last_name",
          "coordination__completed_by__uuid",
          "coordination__completed_at",
          "coordination_participant_profile_ids",
          "direct_delegatee_names",
          "completed_by__uuid",
          "completed_by__user__first_name",
          "completed_by__user__last_name",
          "delegated_by__uuid",
          "delegated_by__user__first_name",
          "delegated_by__user__last_name",
          "completed_checklist_items",
          "original_date",
          "question_result",
          "date",
          "completed_dt",
          "coverage__coveree__user__first_name",
          "coverage__coveree__user__last_name",
          "folder__uuid",
          "folder__title",
          "is_complete",
          "created_by_self",
        ],
        exclude: [
          "template__created_by",
        ],
      },
      staleTime: 0,
    }
  );
  const { data: itemOrderingData, refetch: itemOrderingRefetch } = useGet(
    apiUrls.scheduling.toDoListOrdering(impersonatedProfileUuid)
  );

  const [items, setItems] = useState([]);
  const [completedMap, setCompletedMap] = useState({});
  const [questionResultMap, setQuestionResultMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [templateDialogState, setTemplateDialogState] = useState({
    open: false,
    toDoTemplateId: null,
    readonly: false,
  });
  useEffect(() => {
    if (reloadToken === 0) {
      return;
    }
    refetch();
  }, [reloadToken, refetch]);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (!itemOrderingData) {
      return;
    }

    const folderOrderingMaps = {};
    (itemOrderingData.data || []).forEach((folderOrdering) => {
      const orderingMap = Object.fromEntries(
        (folderOrdering.ordering || []).map((uuid, i) => [uuid, i])
      );
      const folderId = folderOrdering.folder || INBOX_FOLDER_ID;
      folderOrderingMaps[folderId] = orderingMap;
    });

    const sortedByOrdering = [...(data.data || [])].sort((a, b) => {
      const aFolderId = a.folder?.uuid || INBOX_FOLDER_ID;
      const bFolderId = b.folder?.uuid || INBOX_FOLDER_ID;

      if (aFolderId !== bFolderId) {
        return aFolderId.localeCompare(bFolderId);
      }

      const orderingMap = folderOrderingMaps[aFolderId] || {};
      const aOrder = orderingMap[a.uuid];
      const bOrder = orderingMap[b.uuid];

      if (aOrder === undefined && bOrder === undefined) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (aOrder === undefined) return -1;
      if (bOrder === undefined) return 1;

      return aOrder - bOrder;
    });
    setItems(sortedByOrdering);
  }, [data, itemOrderingData]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setCompletedMap(
      Object.fromEntries(data.data.map(({ uuid, is_complete }) => [uuid, is_complete]))
    );
    setQuestionResultMap(
      Object.fromEntries(
        data.data
          .filter((item) => item.template.is_question_format)
          .map(({ uuid, question_result }) => [uuid, question_result])
      )
    );
  }, [data]);

  const handleTodoToggle = async (uuid) => {
    if (isReadOnly) {
      return;
    }
    const selectedItem = items.find((item) => item.uuid === uuid);
    const isCoordinated = Boolean(
      selectedItem?.coordination?.uuid || selectedItem?.template?.is_coordinated
    );
    const isCompleting = !completedMap[uuid];

    if (isCoordinated) {
      try {
        await confirm({
          title: isCompleting
            ? "Complete Coordinated Item"
            : "Reopen Coordinated Item",
          description: isCompleting
            ? "This coordinated item will be marked complete for everyone in this group."
            : "This coordinated item will be reopened for everyone in this group.",
          confirmationText: isCompleting ? "Complete" : "Reopen",
          cancellationText: "Cancel",
        });
      } catch {
        return;
      }
    }

    setLoadingMap((prev) => ({ ...prev, [uuid]: true }));
    try {
      await api.put(apiUrls.scheduling.toDoItem(uuid), {
        is_complete: !completedMap[uuid],
      });
      await refetch();
    } finally {
      setLoadingMap((prev) => ({ ...prev, [uuid]: false }));
    }
  };

  const handleQuestionAnswer = async (uuid, answer) => {
    if (isReadOnly) {
      return;
    }
    setLoadingMap((prev) => ({ ...prev, [uuid]: true }));
    const current = questionResultMap[uuid];
    const finalAnswer = current === answer ? null : answer;
    try {
      await api.put(apiUrls.scheduling.toDoItem(uuid), {
        is_complete: typeof finalAnswer === "boolean",
        question_result: finalAnswer,
      });
      setQuestionResultMap((prev) => ({ ...prev, [uuid]: finalAnswer }));
      setCompletedMap((prev) => ({
        ...prev,
        [uuid]: typeof finalAnswer === "boolean",
      }));
    } finally {
      setLoadingMap((prev) => ({ ...prev, [uuid]: false }));
    }
  };

  const moveItemToFolder = useCallback(
    async (itemId, folderId) => {
      if (isReadOnly) {
        return;
      }
      try {
        setItems((prev) =>
          prev.map((item) =>
            item.uuid === itemId
              ? { ...item, folder: folderId ? { uuid: folderId } : null }
              : item
          )
        );
        await api.put(apiUrls.scheduling.toDoItem(itemId), {
          folder_id: folderId,
        });
      } catch (error) {
        console.error("Failed to move item to folder:", error);
      }
    },
    [isReadOnly]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 0 },
    })
  );

  const handleDragStart = ({ active }) => {
    if (isReadOnly) {
      return;
    }
    setActiveId(active.id);
  };
  const handleDragCancel = () => setActiveId(null);

  const handleItemDragEnd = useCallback(
    (event) => {
      if (isReadOnly) {
        setActiveId(null);
        return;
      }
      const { active, over } = event;

      if (!over || active.id === over.id) {
        setActiveId(null);
        return;
      }

      setItems((current) => {
        const oldIndex = current.findIndex((i) => i.uuid === active.id);
        const newIndex = current.findIndex((i) => i.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(current, oldIndex, newIndex);

          const folderItems = newOrder.filter((item) => {
            if (selectedFolderId === INBOX_FOLDER_ID) {
              return !item.folder;
            }
            return item.folder?.uuid === selectedFolderId;
          });

          const uuidOrder = folderItems.map((i) => i.uuid);
          const folderId =
            selectedFolderId === INBOX_FOLDER_ID ? null : selectedFolderId;

          api.post(apiUrls.scheduling.toDoListOrdering(), {
            folder_id: folderId,
            ordering: uuidOrder,
          });

          itemOrderingRefetch();
          return newOrder;
        }
        return current;
      });

      setActiveId(null);
    },
    [selectedFolderId, itemOrderingRefetch, isReadOnly]
  );

  if (isLoading) {
    return (
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          touchAction: "pan-y",
          p: 0,
          px: 1,
        }}
        dense
      >
        {Array.from({ length: PLACEHOLDER_COUNT }).map((_, idx) => (
          <React.Fragment key={idx}>
            <ListItemButton disableRipple>
              <ListItemIcon>
                <Skeleton variant="circular" width={24} height={24} />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant="text" width="60%" />}
                secondary={<Skeleton variant="text" width="40%" />}
              />
            </ListItemButton>
            {idx < PLACEHOLDER_COUNT - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  }

  return (
    <>
      <ToDoTemplateFormDialog
        open={templateDialogState.open}
        toDoTemplateId={templateDialogState.toDoTemplateId}
        readonly={templateDialogState.readonly}
        onClose={() =>
          setTemplateDialogState({
            open: false,
            toDoTemplateId: null,
            readonly: false,
          })
        }
        onSubmit={() => {
          setTemplateDialogState({
            open: false,
            toDoTemplateId: null,
            readonly: false,
          });
          refetch();
        }}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleItemDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={items.map((item) => item.uuid)}
          strategy={verticalListSortingStrategy}
        >
          <List
            sx={{
              flex: 1,
              overflowY: "auto",
              touchAction: "pan-y",
              p: 0,
              px: 1,
              "& .MuiListItem-root": {
                borderRadius: 1,
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
            dense
          >
            {items.map((item, idx) => (
              <SortableItem key={item.uuid} id={item.uuid} isDisabled={isReadOnly}>
                <ToDoListItem
                  item={item}
                  completed={completedMap[item.uuid]}
                  onToggleComplete={() => handleTodoToggle(item.uuid)}
                  questionResult={questionResultMap[item.uuid]}
                  onAnswer={(ans) => handleQuestionAnswer(item.uuid, ans)}
                  loading={loadingMap[item.uuid]}
                  onItemUpdated={refetch}
                  divider={idx < items.length - 1}
                  folders={folders}
                  onMoveToFolder={(itemId, folderId) =>
                    moveItemToFolder(itemId, folderId)
                  }
                  onViewTemplate={(toDoTemplateId, readonly) =>
                    setTemplateDialogState({
                      open: true,
                      toDoTemplateId,
                      readonly,
                    })
                  }
                  readOnly={isReadOnly}
                />
              </SortableItem>
            ))}

            {items.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: selectedFolderId === INBOX_FOLDER_ID ? 3 : 0,
                    fontSize: "0.9rem",
                  }}
                >
                  {selectedFolderId === INBOX_FOLDER_ID
                    ? "No items in inbox"
                    : "No items in this folder"}
                </Typography>
              </Box>
            )}
          </List>
        </SortableContext>

        <DragOverlay>
          {activeId ? (
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                pointerEvents: "none",
              }}
            >
              {(() => {
                const draggedItem = items.find((i) => i.uuid === activeId);

                if (draggedItem) {
                  return (
                    <ToDoListItem
                      item={draggedItem}
                      completed={completedMap[activeId]}
                      onToggleComplete={() => handleTodoToggle(activeId)}
                      questionResult={questionResultMap[activeId]}
                      onAnswer={(ans) => handleQuestionAnswer(activeId, ans)}
                      loading={loadingMap[activeId]}
                      onItemUpdated={refetch}
                      folders={folders}
                      onMoveToFolder={(itemId, folderId) =>
                        moveItemToFolder(itemId, folderId)
                      }
                      readOnly={isReadOnly}
                    />
                  );
                }
                return null;
              })()}
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}

export default function ToDoList({
  isFullScreen = false,
  selectedFolderId: externalSelectedFolderId,
  onSelectFolder: externalOnSelectFolder,
  folders: externalFolders,
  impersonation: externalImpersonation,
  onImpersonationChange: externalOnImpersonationChange,
}) {
  const { profile } = useProfile();
  const isAdmin = profile?.is_admin;
  const confirm = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const folderDialogDisclosure = useDisclosure();
  const [internalImpersonation, setInternalImpersonation] = useState(null);
  const impersonation =
    externalImpersonation !== undefined
      ? externalImpersonation
      : internalImpersonation;
  const setImpersonation =
    externalOnImpersonationChange || setInternalImpersonation;
  const impersonatedProfileUuid = impersonation?.profileUuid || null;
  const isImpersonating = Boolean(impersonatedProfileUuid);
  const isReadOnly = isImpersonating;

  // Folder state management
  const [internalSelectedFolderId, setInternalSelectedFolderId] =
    useState(INBOX_FOLDER_ID);

  // Use external selectedFolderId if provided (for fullscreen mode), otherwise use internal state
  const selectedFolderId = externalSelectedFolderId || internalSelectedFolderId;
  const setSelectedFolderId =
    externalOnSelectFolder || setInternalSelectedFolderId;
  const { data: folderData, refetch: folderRefetch } = useGet(
    apiUrls.scheduling.toDoFolders(impersonatedProfileUuid),
    {
      params: {
        values: ["uuid", "title", "item_count"],
      },
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    }
  );
  const { data: folderOrderingData, refetch: folderOrderingRefetch } = useGet(
    apiUrls.scheduling.toDoFolderOrdering(impersonatedProfileUuid)
  );
  const [internalFolders, setInternalFolders] = useState(folderData?.data || []);

  // Use external props if provided (for fullscreen mode), otherwise use internal state
  const folders = externalFolders || internalFolders;
  const setFolders = useMemo(
    () => (externalFolders ? () => { } : setInternalFolders),
    [externalFolders]
  ); // No-op if external

  useEffect(() => {
    if (!folderData) {
      return;
    }
    const normalizedFolders = normalizeFolders(folderData.data || []);
    if (folderData && folderOrderingData) {
      const orderingMap = Object.fromEntries(
        (folderOrderingData.data?.ordering || []).map((uuid, i) => [uuid, i])
      );
      const sortedFolders = [...normalizedFolders].sort((a, b) => {
        if (a.uuid === INBOX_FOLDER_ID) return -1;
        if (b.uuid === INBOX_FOLDER_ID) return 1;
        const aOrder = orderingMap[a.uuid];
        const bOrder = orderingMap[b.uuid];
        if (aOrder === undefined && bOrder === undefined) {
          return (a.title || "").localeCompare(b.title || "");
        }
        if (aOrder === undefined) return 1;
        if (bOrder === undefined) return -1;
        return aOrder - bOrder;
      });
      if (!externalFolders) {
        setInternalFolders(sortedFolders);
      }
    } else if (folderData && !externalFolders) {
      setInternalFolders(normalizedFolders);
    }
  }, [folderData, folderOrderingData, externalFolders]);

  

  useEffect(() => {
    if (!isImpersonating) {
      return;
    }
    if (
      selectedFolderId !== INBOX_FOLDER_ID &&
      !folders.some((folder) => folder.uuid === selectedFolderId)
    ) {
      setSelectedFolderId(INBOX_FOLDER_ID);
    }
  }, [folders, isImpersonating, selectedFolderId, setSelectedFolderId]);

  const toDoDisclosure = useDisclosure();
  const manageDisclosure = useDisclosure();
  const expandDisclosure = useDisclosure();
  const completedDisclosure = useDisclosure();
  const [itemsReloadToken, setItemsReloadToken] = useState(0);
  const requestItemsReload = useCallback(
    () => setItemsReloadToken((curr) => curr + 1),
    []
  );

  const handleEditFolder = (folder, updates) => {
    if (isReadOnly) {
      return;
    }
    updateFolder(folder.uuid, updates);
  };

  const handleDeleteFolder = (folderId) => {
    if (isReadOnly) {
      return;
    }
    confirm({
      title: "Delete Folder",
      description:
        "Are you sure you want to delete this folder? Items will be moved to the root level.",
      confirmText: "Delete",
      cancelText: "Cancel",
    }).then(() => {
      deleteFolder(folderId);
    });
  };

  const handleAddItem = () => {
    if (isReadOnly) {
      return;
    }
    toDoDisclosure.onOpen();
  };

  const handleAddFolder = async () => {
    if (isReadOnly) {
      return;
    }
    try {
      const response = await api.post(
        apiUrls.scheduling.toDoFolders(),
        {
        title: "Untitled Folder",
        }
      );
      if (response.data?.success) {
        const newFolder = {
          ...response.data.data,
          isSpecial: false,
          item_count: response.data.data?.item_count ?? 0,
        };
        setFolders((prev) => {
          const normalizedPrev = normalizeFolders(prev);
          const inboxFolder =
            normalizedPrev.find((folder) => folder.uuid === INBOX_FOLDER_ID) ||
            INBOX_FOLDER;
          const nonSpecialFolders = normalizedPrev.filter(
            (folder) => !folder.isSpecial
          );
          return [inboxFolder, newFolder, ...nonSpecialFolders];
        });

        // Update the folder ordering to put the new folder at the top
        const orderedFolderUuids = folders
          .filter((folder) => !folder.isSpecial)
          .map((folder) => folder.uuid);
        const newFolderUuids = [newFolder.uuid, ...orderedFolderUuids];
        try {
          await api.post(
            apiUrls.scheduling.toDoFolderOrdering(),
            {
            ordering: newFolderUuids,
            }
          );
          folderRefetch();
          folderOrderingRefetch();
        } catch (orderError) {
          console.error("Failed to update folder ordering:", orderError);
        }
      }
    } catch (error) {
      console.error("Failed to create folder:", error);
    }
  };

  const updateFolder = async (folderId, updates) => {
    if (isReadOnly) {
      return;
    }
    try {
      console.log("Updating folder:", folderId, updates);
      const response = await api.put(
        apiUrls.scheduling.toDoFolder(folderId),
        updates
      );
      if (response.data?.success) {
        folderRefetch();
      }
    } catch (error) {
      console.error("Failed to update folder:", error);
    }
  };

  const deleteFolder = async (folderId) => {
    if (isReadOnly) {
      return;
    }
    try {
      const response = await api.delete(
        apiUrls.scheduling.toDoFolder(folderId)
      );
      if (response.data?.success) {
        // Remove folder from state
        setFolders((prev) => prev.filter((folder) => folder.uuid !== folderId));
        // If deleted folder was selected, switch to inbox
        if (selectedFolderId === folderId) {
          if (externalOnSelectFolder) {
            externalOnSelectFolder(INBOX_FOLDER_ID);
          } else {
            setInternalSelectedFolderId(INBOX_FOLDER_ID);
          }
        }
        requestItemsReload();
        folderRefetch();
      }
    } catch (error) {
      console.error("Failed to delete folder:", error);
    }
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId);
  };

  const handleFolderReorder = useCallback(
    async (newOrder) => {
      if (isReadOnly) {
        return;
      }
      try {
        setFolders(newOrder);
        const folderUuids = newOrder
          .filter((folder) => !folder.isSpecial)
          .map((folder) => folder.uuid);
        await api.post(
          apiUrls.scheduling.toDoFolderOrdering(),
          {
            ordering: folderUuids,
          }
        );
        folderOrderingRefetch();
      } catch (error) {
        console.error("Failed to reorder folders:", error);
      }
    },
    [folderOrderingRefetch, setFolders, isReadOnly]
  );

  const handleFolderDragEnd = useCallback(
    (event) => {
      if (isReadOnly) {
        return;
      }
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      // Only handle folder reordering
      const activeFolderIndex = folders.findIndex((f) => f.uuid === active.id);
      const overFolderIndex = folders.findIndex((f) => f.uuid === over.id);

      if (activeFolderIndex !== -1 && overFolderIndex !== -1) {
        const newOrder = arrayMove(folders, activeFolderIndex, overFolderIndex);
        handleFolderReorder(newOrder);
      }
    },
    [folders, handleFolderReorder, isReadOnly]
  );


  return (
    <Box sx={{ height: "100%", display: "flex" }}>
      <ToDoTemplateFormDialog
        {...toDoDisclosure.getDisclosureProps()}
        onSubmit={() => {
          toDoDisclosure.onClose();
          requestItemsReload();
          folderRefetch();
        }}
      />
      <ManageToDoDialog
        {...manageDisclosure.getDisclosureProps()}
        onChange={() => requestItemsReload()}
      />
      <CompletedToDoItemsDialog
        open={completedDisclosure.isOpen}
        onClose={completedDisclosure.onClose}
        impersonatedProfileUuid={impersonatedProfileUuid}
        isMobile={isMobile}
        folders={folders}
      />
      <FullScreenDialog
        {...expandDisclosure.getDisclosureProps()}
        fullWidth
        title={
          <ToDoListTitleCard
            isAdmin={isAdmin}
            manageDisclosure={manageDisclosure}
            expandDisclosure={expandDisclosure}
            isFullScreen
            onAddItem={handleAddItem}
            onAddFolder={handleAddFolder}
            isMobile={isMobile}
            onOpenFolders={folderDialogDisclosure.onOpen}
            selectedFolderId={selectedFolderId}
            folders={folders}
            impersonationValue={impersonation}
            onImpersonationChange={setImpersonation}
            onClearImpersonation={() => setImpersonation(null)}
            isReadOnly={isReadOnly}
            onOpenCompleted={completedDisclosure.onOpen}
          />
        }
      >
        <ToDoList
          isFullScreen
          selectedFolderId={selectedFolderId}
          onSelectFolder={handleSelectFolder}
          folders={folders}
          impersonation={impersonation}
          onImpersonationChange={setImpersonation}
        />
      </FullScreenDialog>

      {/* Sidebar */}
      <ToDoSidebar
        folders={folders}
        selectedFolderId={selectedFolderId}
        onSelectFolder={handleSelectFolder}
        onEditFolder={handleEditFolder}
        onDeleteFolder={handleDeleteFolder}
        onAddFolder={handleAddFolder}
        isAdmin={isAdmin}
        isMobile={isMobile}
        folderDialogDisclosure={folderDialogDisclosure}
        onFolderDragEnd={handleFolderDragEnd}
        isReadOnly={isReadOnly}
      />

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        {!isFullScreen && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: "background.paper",
            }}
          >
            <ToDoListTitleCard
              isAdmin={isAdmin}
              manageDisclosure={manageDisclosure}
              expandDisclosure={expandDisclosure}
              isFullScreen={false}
              onAddItem={handleAddItem}
              isMobile={isMobile}
              onOpenFolders={folderDialogDisclosure.onOpen}
              selectedFolderId={selectedFolderId}
              folders={folders}
              impersonationValue={impersonation}
              onImpersonationChange={setImpersonation}
              onClearImpersonation={() => setImpersonation(null)}
              isReadOnly={isReadOnly}
              onOpenCompleted={completedDisclosure.onOpen}
            />
          </Box>
        )}

        {/* Tasks list */}
        <ToDoItemsList
          selectedFolderId={selectedFolderId}
          impersonatedProfileUuid={impersonatedProfileUuid}
          isReadOnly={isReadOnly}
          folders={folders}
          reloadToken={itemsReloadToken}
        />
      </Box>
    </Box>
  );
}
